'use client';
import React, { useCallback, memo } from 'react';
import Supercluster, { ClusterProperties } from 'supercluster';
import { FeaturesClusterMarker } from './FeaturesClusterMarker';
import { FeatureMarker } from './FeatureMarker';
import { FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import { SingleStoreType } from '@workspace/interfaces';
import { useSupercluster } from '@workspace/widgets/MapView/hooks';
import { useMap } from '@vis.gl/react-google-maps';

type ClusteredMarkersProps = {
  geojson: FeatureCollection<Point, SingleStoreType>;
  setInfoWindowData: (store: SingleStoreType) => void;
  superclusterOptions: Supercluster.Options<GeoJsonProperties, ClusterProperties>;
};

const ClusteredMarkersComponent = ({ geojson, setInfoWindowData, superclusterOptions }: ClusteredMarkersProps) => {
  const { clusters, getLeaves } = useSupercluster(geojson, superclusterOptions);
  const map = useMap();

  const handleMarkerClick = useCallback(
    (_marker: google.maps.marker.AdvancedMarkerElement, featureId: string) => {
      const feature = clusters.find((feat) => feat.id === featureId);

      if (feature) {
        setInfoWindowData(feature.properties as SingleStoreType);
      }
    },
    [clusters, setInfoWindowData],
  );

  const handleClusterClick = useCallback(
    (_marker: google.maps.marker.AdvancedMarkerElement, clusterId: number) => {
      const leaves = getLeaves(clusterId);

      // Optionally, you can implement zooming into the cluster or displaying a list of stores
      if (leaves.length > 0 && map) {
        const bounds = new google.maps.LatLngBounds();
        leaves.forEach((leaf) => {
          const [lng, lat] = leaf.geometry.coordinates;
          bounds.extend(new google.maps.LatLng(lat, lng));
        });
        map.fitBounds(bounds);
      }
    },
    [getLeaves, map],
  );

  return (
    <>
      {clusters.map((feature) => {
        const [lng, lat] = feature.geometry.coordinates;

        const clusterProperties = feature.properties as ClusterProperties;
        const markerProperties = feature.properties as SingleStoreType;
        const isCluster: boolean = clusterProperties.cluster;

        return isCluster ? (
          <FeaturesClusterMarker
            key={feature.id}
            title={`${clusterProperties.point_count_abbreviated} stores`}
            clusterId={clusterProperties.cluster_id}
            position={{ lat, lng }}
            size={clusterProperties.point_count}
            sizeAsText={String(clusterProperties.point_count_abbreviated)}
            onMarkerClick={handleClusterClick}
          />
        ) : (
          <FeatureMarker
            key={feature.id}
            title={markerProperties.storeName}
            featureId={feature.id as string}
            position={{ lat, lng }}
            onMarkerClick={handleMarkerClick}
          />
        );
      })}
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders when props haven't changed
export const ClusteredMarkers = memo(ClusteredMarkersComponent);
